//import vue
import type { Ref } from "vue";
import { isRef } from "vue";
//import types
import type { ToggleDisplay } from "@/typings/toggleDisplay";
/**
 * @function useToggleDisplay - toggle the display property of an html element
 * @returns - function that realizes the toggling
 */
export default function useToggleDisplay(
  initialElement: Ref<null | HTMLElement> | undefined = undefined
): ToggleDisplay {
  /**
   * @function getDisplayValue - get the string value of the display property
   * @param {string} oldValue - the old value of the property
   * @param {boolean | string | undefined} newValue - new value of the property
   * @param {string} defaultValue - default value for the property, normally block but can change
   * @returns {string} new value for the display property
   */
  function getDisplayValue(
    oldValue: string,
    newValue: boolean | string | undefined,
    defaultValue = "block"
  ): string {
    //if the new value is empty, toggle the property
    if (newValue == undefined) {
      //if the old value is none, get the default
      if (oldValue == "none") {
        return defaultValue;
      }
      //if the old value is different than none, set it to none
      else {
        return "none";
      }
    }
    //if the new Value is false set it to none as its the negative value
    else if (newValue == false) {
      return "none";
    }
    //if the new value is true, set it to the default as is the positive value
    else if (newValue == true) {
      return defaultValue;
    }
    //if the new value is a string set it, the typing is not verified so be careful
    else {
      return newValue;
    }
  }

  function toggleDisplay(
    value: boolean | string | undefined = undefined,
    element: Ref<null | HTMLElement> | HTMLElement | undefined = undefined,
    defaultValue = "block"
  ) {
    //if an element is provided
    if (element) {
      //if the element is a template ref and its not null
      if (isRef(element)) {
        if (element.value != null) {
          //change the display according to the received values
          element.value.style.display = getDisplayValue(
            element.value.style.display,
            value,
            defaultValue
          );
        }
      }
      //if the element is just an html element
      else {
        //change the display according to the received values
        element.style.display = getDisplayValue(
          element.style.display,
          value,
          defaultValue
        );
      }
    }
    //if theres no element provided use the default element
    else {
      //if the initial element was defined
      if (initialElement) {
        if (initialElement.value != null) {
          //change the display according to the received values
          initialElement.value.style.display = getDisplayValue(
            initialElement.value.style.display,
            value,
            defaultValue
          );
        }
      }
    }
  }

  return toggleDisplay;
}
